import {
    ArrowDownOutlined,
    ArrowUpOutlined,
    BarChartOutlined,
    BellOutlined,
    DeleteOutlined,
    EditOutlined,
    FileImageOutlined,
    FileOutlined,
    MoreOutlined,
    MutedOutlined,
    PlusOutlined,
    PushpinOutlined
} from '@ant-design/icons'
import { useChannels } from '@entities/channel'
import { checkDraftPost, savePayload } from '@entities/post/api'
import { basePostType } from '@shared/lib/constants/constants'
import { getMessageType } from '@shared/lib/getMessageType/getMessageType'
import { randomId } from '@shared/lib/randomId/randomId'
import { validateLength } from '@shared/lib/validatorLength/validatorLength'
import { CustomTag } from '@shared/ui/custom-tag/custom-tag'
import { CustomUpload } from '@shared/ui/custom-upload/custom-upload'
import { IconSwitch } from '@shared/ui/icon-switch/icon-switch'
import { TextEditor } from '@shared/ui/text-editor/text-editor'
import type { FormProps, UploadFile, UploadProps } from 'antd'
import {
    Avatar,
    Button,
    Col,
    DatePicker,
    Dropdown,
    Flex,
    Form,
    Input,
    Modal,
    Row,
    Select,
    Space,
    Switch,
    theme,
    Typography
} from 'antd'
import type { UploadChangeParam } from 'antd/lib/upload'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { useCallback, useEffect, useRef, useState } from 'react'
import { usePosts } from '../../model/hooks/use-posts'
import type { FileDTO, Message, Post, PostState } from '../../model/types'
import { MessageType } from '../../model/types'

const { Text, Paragraph } = Typography
interface EditPostModalProps {
    open: boolean
    editPostData: Post
    onClose: () => void
}

type FieldType = {
    title: string
    state: PostState
    channels: number[]
    messages: Message[]
    publishDate: Dayjs
    deleteDate: Dayjs
    notify: boolean
    isPinned: boolean
    postType: {
        value: number
    }
}

const BOT_USERNAME = process.env.BOT_USERNAME || ''

export const EditPostModal = ({ open, onClose, editPostData }: EditPostModalProps) => {
    const {
        actions: { editPost },
        postTypes
    } = usePosts()

    const { channels } = useChannels()

    const {
        token: { colorTextLabel }
    } = theme.useToken()

    const isPublished = editPostData.state === 'published'

    const [form] = Form.useForm()

    const onFinish: FormProps<FieldType>['onFinish'] = useCallback(
        async (values: FieldType) => {
            editPost({
                ...values,
                id: editPostData.id,
                publishDate: values.publishDate.toDate()
            })

            onClose()
        },
        [editPost, editPostData.id, onClose]
    )

    const onSubmit = useCallback(
        (state?: Partial<FieldType>) => {
            if (state) {
                form.setFieldsValue({
                    ...state
                })
            }

            form.submit()
        },
        [form]
    )

    useEffect(() => {
        form.resetFields()
    }, [form, open])

    const handleDelete = useCallback((remove: (name: number) => void, name: number) => {
        remove(name)
    }, [])

    const normalizeFile = (e: UploadChangeParam<UploadFile<FileDTO>>) => (Array.isArray(e) ? e : e?.fileList)

    const getUploadProps = ({ accept, multiple = false }: UploadProps<FileDTO>): UploadProps<FileDTO> => ({
        multiple,
        maxCount: multiple ? 99 : 1,
        action: '/api/posts/uploads',
        listType: 'picture-card',
        accept
    })

    const handleSetMultiValue = (index: number, value: boolean) => {
        const messages = form.getFieldValue('messages') || []

        messages[index] = {
            ...messages[index],
            isCaptionAbove: value
        }

        form.setFieldsValue({ messages })
    }

    const [repostModalOpen, setRepostModalOpen] = useState(false)
    const [answerModalOpen, setAnswerModalOpen] = useState(false)

    const [createPostUsingBot, setCreatePostUsingBot] = useState(false)

    const intervalRef = useRef<NodeJS.Timeout | null>(null)

    const [postVersion, setPostVersion] = useState(0)

    const onCreatePostUsingBot = async () => {
        const sessionId = randomId()
        const payloadId = await savePayload({
            action: 'edit_post',
            session_id: sessionId,
            post_id: editPostData.id,
            platform: 'desktop'
        })

        window.open(`https://t.me/${BOT_USERNAME}?start=payload_${payloadId}`)

        intervalRef.current = setInterval(async () => {
            const postData = await checkDraftPost(sessionId)

            if (postData) {
                if (postVersion !== postData.version) {
                    form.setFieldsValue({
                        messages: [
                            {
                                ...postData.post,
                                id: Date.now(),
                                file: postData.post.file.map((file) => {
                                    const item = {
                                        ...file,
                                        name: file.response.file_name,
                                        type: file.response.file_type,
                                        url: file.response.file_url
                                    }
                                    if (
                                        file.response.file_type !== MessageType.AUDIO &&
                                        file.response.file_type !== MessageType.VOICE &&
                                        file.response.file_type !== MessageType.DOCUMENT
                                    ) {
                                        item.thumbUrl = file.response?.preview_url || file.response.file_url
                                    }
                                    return item
                                })
                            }
                        ]
                    })
                    setCreatePostUsingBot(false)
                    setPostVersion(postData.version)
                }
            }
        }, 3000)
    }

    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
        }
    }, [])

    return (
        <>
            <Modal
                open={open}
                centered
                onCancel={onClose}
                title="Редактирование поста"
                footer={[
                    <Button key="back" onClick={onClose}>
                        Отменить
                    </Button>,
                    <Button key="submit" type="primary" onClick={() => onSubmit()}>
                        Сохранить
                    </Button>
                ]}
            >
                <Form
                    form={form}
                    name="add-post"
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                    onFinish={onFinish}
                    initialValues={{
                        ...editPostData,
                        postType: {
                            label: editPostData.postType?.name,
                            value: editPostData.postType?.value
                        },
                        publishDate: dayjs(editPostData?.publishDate),
                        messages: editPostData?.messages.map((item) => ({
                            ...item,
                            file: item.file.map((file) => {
                                const item = {
                                    ...file,
                                    name: file.response.file_name,
                                    type: file.response.file_type,
                                    url: file.response.file_url
                                }
                                if (
                                    file.response.file_type !== MessageType.AUDIO &&
                                    file.response.file_type !== MessageType.VOICE &&
                                    file.response.file_type !== MessageType.DOCUMENT
                                ) {
                                    item.thumbUrl = file.response?.preview_url || file.response.file_url
                                }
                                return item
                            })
                        }))
                    }}
                >
                    <Row gutter={8} style={{ marginBottom: 16 }}>
                        <Col>
                            <CustomTag color={basePostType.color}>{basePostType.name}</CustomTag>
                        </Col>
                        <Col>
                            <Form.Item<FieldType> name="postType" required={false} style={{ marginBottom: 0 }}>
                                <Select
                                    placeholder="Выберите тип"
                                    labelInValue
                                    optionLabelProp="label"
                                    popupMatchSelectWidth={false}
                                >
                                    {postTypes?.map((item) => (
                                        <Select.Option
                                            style={{ textAlign: 'center' }}
                                            key={item.id}
                                            value={item.id}
                                            label={item.name}
                                        >
                                            <CustomTag color={item.color}>{item.name}</CustomTag>
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item<FieldType> name="state" hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item<FieldType> label="Введите название поста" name="title" hidden initialValue={'Title'}>
                        <Input />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item<FieldType>
                                label="Выберите каналы"
                                name="channels"
                                required={false}
                                rules={[{ required: true, message: 'Обязательное поле' }]}
                            >
                                <Select
                                    showSearch
                                    mode="multiple"
                                    optionFilterProp="label"
                                    style={{ width: '100%' }}
                                    popupMatchSelectWidth={false}
                                >
                                    {channels?.map((c) => (
                                        <Select.Option value={c.id} key={c.id}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <Avatar src={c.photoUrl} size={20} />
                                                {c.name}
                                            </div>
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={'12'} style={{ position: 'relative' }}>
                            <Form.Item<FieldType>
                                label="Время публикации"
                                name="publishDate"
                                required={false}
                                rules={[{ required: true, message: 'Обязательное поле' }]}
                            >
                                <DatePicker
                                    showTime
                                    showSecond={false}
                                    style={{ width: '100%' }}
                                    format={[
                                        'DD/MM/YYYY HH:mm',
                                        'DD/MM/YY HH:mm',
                                        'DD.MM.YYYY HH:mm',
                                        'DD.MM.YY HH:mm'
                                    ]}
                                    minDate={dayjs()}
                                    placeholder="Выберите дату публикации"
                                />
                            </Form.Item>
                            <Form.Item<FieldType>
                                name="isPinned"
                                required={false}
                                style={{ position: 'absolute', top: 0, right: 0 }}
                            >
                                <IconSwitch text={'Закрепить'}>
                                    <PushpinOutlined />
                                </IconSwitch>
                            </Form.Item>
                            <Form.Item<FieldType>
                                name="notify"
                                required={false}
                                style={{ position: 'absolute', top: 0, right: '26px' }}
                            >
                                <IconSwitch text={'Отправить со звуком'}>
                                    <BellOutlined />
                                </IconSwitch>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.List name="messages">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }, index) => {
                                    const defaultType = form.getFieldValue('messages')[index].messageType
                                    const multi = form.getFieldValue('messages')[index].multi
                                    const messages = form.getFieldValue('messages')
                                    const isCaptionAbove = form.getFieldValue('messages')[index]?.isCaptionAbove
                                    const item =
                                        (messages[0]?.file && getMessageType(messages[0]?.file[0])) || defaultType
                                    const captionItem = (
                                        <>
                                            <Flex
                                                style={{
                                                    marginBottom: '10px'
                                                }}
                                                gap="middle"
                                                justify={'space-between'}
                                                align="center"
                                            >
                                                <Text style={{ color: colorTextLabel }}>
                                                    Введите подпись(макс. 1024 символов)
                                                </Text>
                                                {!isCaptionAbove && (
                                                    <Button
                                                        type="primary"
                                                        onClick={() => handleSetMultiValue(index, true)}
                                                        icon={<ArrowUpOutlined />}
                                                    />
                                                )}
                                            </Flex>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'text']}
                                                required={false}
                                                rules={[
                                                    {
                                                        validator: validateLength({ length: 1024 })
                                                    }
                                                ]}
                                            >
                                                <TextEditor />
                                            </Form.Item>
                                            {isCaptionAbove && (
                                                <Button
                                                    style={{
                                                        position: 'absolute',
                                                        right: 0,
                                                        zIndex: 1
                                                    }}
                                                    type="primary"
                                                    onClick={() => handleSetMultiValue(index, false)}
                                                    icon={<ArrowDownOutlined />}
                                                />
                                            )}
                                        </>
                                    )

                                    const moreBtn = !isPublished && (
                                        <Dropdown
                                            trigger={['click']}
                                            menu={{
                                                items: [
                                                    {
                                                        key: 1,
                                                        label: (
                                                            <Space
                                                                style={{ width: '100%' }}
                                                                onClick={() => setRepostModalOpen(true)}
                                                            >
                                                                Репост
                                                            </Space>
                                                        )
                                                    },
                                                    {
                                                        key: 2,
                                                        label: (
                                                            <Space
                                                                style={{ width: '100%' }}
                                                                onClick={() => setAnswerModalOpen(true)}
                                                            >
                                                                Ответ
                                                            </Space>
                                                        )
                                                    },
                                                    { type: 'divider' },
                                                    {
                                                        key: 3,
                                                        label: 'Тип сообщения',
                                                        type: 'group',
                                                        children: [
                                                            {
                                                                key: 4,
                                                                label: (
                                                                    <Space>
                                                                        <EditOutlined /> Текст
                                                                    </Space>
                                                                ),
                                                                onClick: () => {
                                                                    handleDelete(remove, name)
                                                                    add({
                                                                        id: Date.now(),
                                                                        messageType: MessageType.TEXT
                                                                    })
                                                                }
                                                            },
                                                            {
                                                                key: 5,
                                                                label: (
                                                                    <Space>
                                                                        <BarChartOutlined /> Опрос
                                                                    </Space>
                                                                ),
                                                                onClick: () => {
                                                                    handleDelete(remove, name)
                                                                    add({
                                                                        id: Date.now(),
                                                                        messageType: MessageType.QUIZ
                                                                    })
                                                                }
                                                            },
                                                            {
                                                                key: 6,
                                                                label: (
                                                                    <Space>
                                                                        <FileImageOutlined /> Фото и Видео
                                                                    </Space>
                                                                ),
                                                                onClick: () => {
                                                                    handleDelete(remove, name)
                                                                    add({
                                                                        id: Date.now(),
                                                                        messageType: MessageType.PHOTO,
                                                                        multi: true,
                                                                        isCaptionAbove: false
                                                                    })
                                                                }
                                                            },
                                                            {
                                                                key: 7,
                                                                label: (
                                                                    <Space>
                                                                        <MutedOutlined /> Аудио
                                                                    </Space>
                                                                ),
                                                                onClick: () => {
                                                                    handleDelete(remove, name)
                                                                    add({
                                                                        id: Date.now(),
                                                                        messageType: MessageType.AUDIO,
                                                                        multi: true,
                                                                        isCaptionAbove: false
                                                                    })
                                                                }
                                                            },
                                                            {
                                                                key: 8,
                                                                label: (
                                                                    <Space>
                                                                        <FileOutlined /> Документы
                                                                    </Space>
                                                                ),
                                                                onClick: () => {
                                                                    handleDelete(remove, name)
                                                                    add({
                                                                        id: Date.now(),
                                                                        messageType: MessageType.DOCUMENT,
                                                                        multi: true,
                                                                        isCaptionAbove: false
                                                                    })
                                                                }
                                                            }
                                                        ]
                                                    }
                                                ]
                                            }}
                                        >
                                            <Button type="text" style={{ padding: '4px' }}>
                                                <MoreOutlined />
                                            </Button>
                                        </Dropdown>
                                    )

                                    return (
                                        <div key={key} style={{ marginBottom: 16, position: 'relative' }}>
                                            <Modal
                                                title="Репост сообщения"
                                                open={repostModalOpen}
                                                onCancel={() => setRepostModalOpen(false)}
                                                onOk={() => {
                                                    form.validateFields([name, 'repostLink']).then(() =>
                                                        setRepostModalOpen(false)
                                                    )
                                                }}
                                                centered
                                            >
                                                <Paragraph>
                                                    Ссылка на сообщение в формате t.me/username/123 или t.me/c/123/123.
                                                    Для репоста канала или чата нужно чтобы боты был в админке.
                                                </Paragraph>
                                                <Form.Item
                                                    name={[name, 'repostLink']}
                                                    required={false}
                                                    rules={[{ required: true, message: 'Обязательное поле' }]}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Modal>
                                            <Modal
                                                title="Ответ на сообщение"
                                                open={answerModalOpen}
                                                onCancel={() => setAnswerModalOpen(false)}
                                                onOk={() => {
                                                    form.validateFields([name, 'replyLink']).then(() =>
                                                        setAnswerModalOpen(false)
                                                    )
                                                }}
                                                centered
                                            >
                                                <Paragraph>
                                                    Ссылка на сообщение в формате t.me/username/123 или t.me/c/123/123.
                                                </Paragraph>
                                                <Form.Item
                                                    name={[name, 'replyLink']}
                                                    required={false}
                                                    rules={[{ required: true, message: 'Обязательное поле' }]}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Modal>
                                            <div style={{ position: 'relative' }}>
                                                <Space
                                                    style={{
                                                        position: 'absolute',
                                                        top: '4px',
                                                        right: 0,
                                                        zIndex: 1
                                                    }}
                                                >
                                                    <Text>Редактировать пост через бота</Text>
                                                    <Switch
                                                        value={createPostUsingBot}
                                                        onChange={() => setCreatePostUsingBot((prev) => !prev)}
                                                    />
                                                </Space>
                                                {createPostUsingBot ? (
                                                    <div
                                                        style={{
                                                            padding: '40px 50px 50px 50px',
                                                            textAlign: 'center'
                                                        }}
                                                    >
                                                        <Button size="large" onClick={onCreatePostUsingBot}>
                                                            Перейти в бот
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        {item === MessageType.TEXT && (
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'text']}
                                                                label={
                                                                    <Space size={2}>
                                                                        <Text style={{ color: colorTextLabel }}>
                                                                            Содержание сообщения
                                                                        </Text>
                                                                        {moreBtn}
                                                                    </Space>
                                                                }
                                                                required={false}
                                                                rules={[
                                                                    {
                                                                        required: true,
                                                                        message: 'Содержание обязательно',
                                                                        validator: validateLength({
                                                                            length: 4096,
                                                                            required: true
                                                                        })
                                                                    }
                                                                ]}
                                                            >
                                                                <TextEditor maxLength={4096} />
                                                            </Form.Item>
                                                        )}
                                                        {(item === MessageType.PHOTO || item === MessageType.VIDEO) && (
                                                            <>
                                                                {isCaptionAbove && captionItem}
                                                                <Form.Item
                                                                    {...restField}
                                                                    name={[name, 'file']}
                                                                    valuePropName="fileList"
                                                                    getValueFromEvent={normalizeFile}
                                                                    label={
                                                                        <Space size={2}>
                                                                            <Text style={{ color: colorTextLabel }}>
                                                                                Фото и Видео
                                                                            </Text>{' '}
                                                                            {moreBtn}
                                                                        </Space>
                                                                    }
                                                                    required={false}
                                                                    rules={[
                                                                        { required: true, message: 'Файл обязателен' }
                                                                    ]}
                                                                >
                                                                    <CustomUpload
                                                                        {...getUploadProps({
                                                                            accept: 'image/*, video/*',
                                                                            multiple: multi
                                                                        })}
                                                                    />
                                                                </Form.Item>
                                                                {!isCaptionAbove && captionItem}
                                                            </>
                                                        )}
                                                        {item === MessageType.DOCUMENT && (
                                                            <>
                                                                {isCaptionAbove && captionItem}
                                                                <Form.Item
                                                                    {...restField}
                                                                    name={[name, 'file']}
                                                                    valuePropName="fileList"
                                                                    getValueFromEvent={normalizeFile}
                                                                    label={
                                                                        <Space size={2}>
                                                                            <Text style={{ color: colorTextLabel }}>
                                                                                Документ
                                                                            </Text>{' '}
                                                                            {moreBtn}
                                                                        </Space>
                                                                    }
                                                                    required={false}
                                                                    rules={[
                                                                        { required: true, message: 'Файл обязателен' }
                                                                    ]}
                                                                >
                                                                    <CustomUpload
                                                                        {...getUploadProps({
                                                                            accept: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,*.*',
                                                                            multiple: multi
                                                                        })}
                                                                    />
                                                                </Form.Item>
                                                                {!isCaptionAbove && captionItem}
                                                            </>
                                                        )}
                                                        {item === MessageType.AUDIO && (
                                                            <>
                                                                {isCaptionAbove && captionItem}
                                                                <Form.Item
                                                                    {...restField}
                                                                    name={[name, 'file']}
                                                                    valuePropName="fileList"
                                                                    getValueFromEvent={normalizeFile}
                                                                    label={
                                                                        <Space size={2}>
                                                                            <Text style={{ color: colorTextLabel }}>
                                                                                Аудио
                                                                            </Text>{' '}
                                                                            {moreBtn}
                                                                        </Space>
                                                                    }
                                                                    required={false}
                                                                    rules={[
                                                                        { required: true, message: 'Файл обязателен' }
                                                                    ]}
                                                                >
                                                                    <CustomUpload
                                                                        {...getUploadProps({ accept: 'audio/*, .ogg' })}
                                                                    />
                                                                </Form.Item>
                                                                {!isCaptionAbove && captionItem}
                                                            </>
                                                        )}
                                                        {item === MessageType.QUIZ && !isPublished && (
                                                            <>
                                                                <Form.Item
                                                                    {...restField}
                                                                    name={[name, 'question']}
                                                                    label={
                                                                        <Space size={2}>
                                                                            <Text style={{ color: colorTextLabel }}>
                                                                                Заголовок опроса
                                                                            </Text>{' '}
                                                                            {moreBtn}
                                                                        </Space>
                                                                    }
                                                                    rules={[
                                                                        {
                                                                            required: true,
                                                                            message: 'Заголовок обязателен'
                                                                        }
                                                                    ]}
                                                                >
                                                                    <Input placeholder="Введите заголовок опроса" />
                                                                </Form.Item>

                                                                <Form.List name={[name, 'options']}>
                                                                    {(fields, { add, remove }) => (
                                                                        <>
                                                                            {fields.map(
                                                                                ({ key, name, ...restField }) => (
                                                                                    <Form.Item
                                                                                        key={key}
                                                                                        {...restField}
                                                                                        name={name}
                                                                                        rules={[
                                                                                            {
                                                                                                required: true,
                                                                                                message:
                                                                                                    'Вариант обязателен'
                                                                                            }
                                                                                        ]}
                                                                                    >
                                                                                        <Input
                                                                                            placeholder={`Введите текст варианта ${name + 1}`}
                                                                                            addonAfter={
                                                                                                <Button
                                                                                                    style={{
                                                                                                        height: 'auto'
                                                                                                    }}
                                                                                                    type="link"
                                                                                                    onClick={() =>
                                                                                                        remove(name)
                                                                                                    }
                                                                                                    icon={
                                                                                                        <DeleteOutlined />
                                                                                                    }
                                                                                                />
                                                                                            }
                                                                                        />
                                                                                    </Form.Item>
                                                                                )
                                                                            )}
                                                                            <Form.Item>
                                                                                <Button
                                                                                    type="dashed"
                                                                                    onClick={() => add()}
                                                                                    block
                                                                                    icon={<PlusOutlined />}
                                                                                >
                                                                                    Добавить вариант
                                                                                </Button>
                                                                            </Form.Item>
                                                                        </>
                                                                    )}
                                                                </Form.List>
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </>
                        )}
                    </Form.List>
                </Form>
            </Modal>
        </>
    )
}
